/**
 * Interactive Validators — Drag-drop, matching, ordering, and categorization question types.
 *
 * Extracted from validatorRegistry.ts during R1-S1 god-file split.
 * Covers: ordering, matching, drag_drop_fill, table_reading, schedule_reading, categorize.
 */

import type { ScheduleEvent } from '../../types';
import type { ValidatorFn } from './shared';

// ── Ordering ─────────────────────────────────────────────────

export const validateOrdering: ValidatorFn = (userAnswer, question) => {
  const userIndices = String(userAnswer).split(',').map(Number);
  const config = question.data || {};
  // Support both flat and nested data formats:
  // Flat: data.correctOrder   Nested: data.ordering.correctOrder
  const orderingData = config.ordering || config;
  const correctOrder = orderingData.correctOrder || [];
  const isCorrect = userIndices.length === correctOrder.length && userIndices.every((val: number, index: number) => val === correctOrder[index]);
  return {
    isCorrect,
    distractorIndex: isCorrect ? undefined : 0
  };
};

// ── Matching ─────────────────────────────────────────────────

export const validateMatching: ValidatorFn = (userAnswer, question) => {
  let userPairs: [number, number][] = [];
  try {
    userPairs = JSON.parse(String(userAnswer));
  } catch {

    /* empty */}
  const config = question.data || {};
  // Support multiple data formats for expected pair count:
  // Format A: data.pairs   Format B: data.matching.pairs   Format C: data.leftItems
  const expectedLength = config.pairs?.length || config.matching?.pairs?.length || config.leftItems?.length || 0;
  const isCorrect = userPairs.every(([left, right]) => left === right) && userPairs.length === expectedLength;
  return {
    isCorrect,
    distractorIndex: isCorrect ? undefined : 0
  };
};

// ── Drag & Drop Fill ─────────────────────────────────────────

export const validateDragDropFill: ValidatorFn = (userAnswer, question) => {
  let userAnswers: Record<string, string> = {};
  try {
    userAnswers = JSON.parse(String(userAnswer));
  } catch {

    /* empty */}
  const config = question.data || {};
  let blanks: Array<{
    id: string;
    correctAnswer: string;
  }> = config.blanks || [];

  // Format B: draggables + correctPlacements (no blanks array)
  if (blanks.length === 0 && config.draggables && config.correctPlacements) {
    const draggables = config.draggables as string[];
    const placements = config.correctPlacements as Record<string, number>;
    blanks = Object.entries(placements).map(([id, dragIndex]) => ({
      id,
      correctAnswer: draggables[dragIndex] || ''
    }));
  }
  const isCorrect = blanks.length > 0 && blanks.every(blank => userAnswers[blank.id] === blank.correctAnswer);
  return {
    isCorrect,
    distractorIndex: isCorrect ? undefined : 0
  };
};

// ── Table Reading ────────────────────────────────────────────

export const validateTableReading: ValidatorFn = (userAnswer, question) => {
  const config = question.data || {};
  const isCorrect = Number(userAnswer) === config.correctSelection;
  return {
    isCorrect,
    distractorIndex: isCorrect ? undefined : 0
  };
};

// ── Schedule Reading ─────────────────────────────────────────

export const validateScheduleReading: ValidatorFn = (userAnswer, question) => {
  const config = question.data || {};
  const correctCell = config.correctCell;
  const schedule = config.schedule || {
    columns: [],
    timeSlots: [],
    events: []
  };
  if (!correctCell) return {
    isCorrect: false,
    distractorIndex: 0
  };
  const [userRow, userCol] = String(userAnswer).split('-').map(Number);
  const isCorrect = userRow === correctCell.row && userCol === correctCell.col;
  const findEvent = (row: number, col: number) => schedule.events?.find((e: ScheduleEvent) => e.row === row && e.col === col);
  const userEvent = findEvent(userRow, userCol);
  const correctEvent = findEvent(correctCell.row, correctCell.col);
  const userLabel = userEvent?.content || `rij ${userRow + 1}, kolom ${userCol + 1}`;
  const correctLabel = correctEvent?.content || `rij ${correctCell.row + 1}, kolom ${correctCell.col + 1}`;
  const distractorKey = userRow * schedule.columns.length + userCol;
  return {
    isCorrect,
    distractorIndex: distractorKey,
    displayAnswer: isCorrect ? correctLabel : `${userLabel} (juiste antwoord: ${correctLabel})`,
    correctAnswerDisplay: correctLabel
  };
};

// ── Categorize ───────────────────────────────────────────────

export const validateCategorize: ValidatorFn = (userAnswer, question) => {
  let userAssignments: Record<string, number> = {};
  try {
    userAssignments = JSON.parse(String(userAnswer));
  } catch {

    /* empty */}
  const config = question.data || {};
  const items: Array<{
    text: string;
    correctCategory: number;
  }> = config.items || [];
  let correctCount = 0;
  const isCorrect = Object.keys(userAssignments).length === items.length && items.every((item, idx) => {
    // Defensive: coerce both sides to number in case of string/number mismatch
    const match = Number(userAssignments[String(idx)]) === Number(item.correctCategory);
    if (match) correctCount++;
    return match;
  });
  return {
    isCorrect,
    distractorIndex: isCorrect ? undefined : 0,
    displayAnswer: `${correctCount}/${items.length} correct`
  };
};